import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Play, ChevronRight, CheckIcon, Target } from 'lucide-react';
import { useCourses, CourseWithProgress } from '@/hooks/useCourses';
import { Spinner } from '@/components/ui/loading-spinner';
import { usePlaybookProgress } from '@/hooks/usePlaybookProgress';
import { getDemoEnrollmentData } from '../services/enrollmentService';

const CourseDetailPage = () => {
  const { id: projectId, playbookId } = useParams<{ id: string; playbookId: string }>();
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const { useGetCourseDetails } = useCourses();
  const { data: course, isLoading, error } = useGetCourseDetails(playbookId || '');
  
  // Get progress tracking data with project context
  const { 
    calculateTotalProgress, 
    setActivePhase, 
    completedTasks 
  } = usePlaybookProgress(projectId);
  
  // Get the course progress using the hook's data instead of local calculation
  const courseProgress = course ? calculateTotalProgress() : { percentage: 0, completed: 0, total: 0 };
  
  // Get consistent enrollment data for this course/playbook
  const demoEnrolledUsers = course ? getDemoEnrollmentData(
    course.id, 
    (course as any).display_order || 0
  ) : { avatars: [], count: 0 };
  
  if (isLoading) {
    return (
      <div className="container py-4 sm:py-8 max-w-[1120px] mx-auto bg-white dark:bg-zinc-900 min-h-screen px-4 sm:px-6">
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8 text-blue-500 dark:text-blue-400" />
        </div>
      </div>
    );
  }
  
  if (error || !course) {
    return (
      <div className="container py-4 sm:py-8 max-w-[1120px] mx-auto bg-white dark:bg-zinc-900 min-h-screen px-4 sm:px-6">
        <div className="text-center py-12">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Course not found</h2>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400 px-4">
            {error ? error.message : "The course you're looking for doesn't exist or has been removed."}
          </p>
          <Button 
            className="mt-6"
            onClick={() => navigate(`/projects/${projectId}/playbooks`)}
          >
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }
  
  // Start a course (go to first phase)
  const handleStartCourse = async () => {
    if (!course || !course.phases || course.phases.length === 0) return;
    
    const firstPhase = course.phases[0];
    await setActivePhase(course.id, firstPhase.id);
    navigate(`/projects/${projectId}/playbooks/${course.id}/active`);
  };
  
  // Continue a course (go to active or first incomplete phase)
  const handleContinueCourse = async () => {
    if (!course || !course.phases || course.phases.length === 0) return;
    
    // Find the first phase with incomplete tasks in order
    // Exclude Pro Tip tasks when checking for incomplete tasks
    for (const phase of course.phases) {
      const hasIncompleteTask = phase.tasks?.some(task => 
        task.title !== 'Pro Tip' && !completedTasks[task.id]
      );
      
      if (hasIncompleteTask) {
        await setActivePhase(course.id, phase.id);
        navigate(`/projects/${projectId}/playbooks/${course.id}/active?phase=${phase.id}`);
        return;
      }
    }
    
    // If all tasks are complete, go to the last phase
    const lastPhase = course.phases[course.phases.length - 1];
    await setActivePhase(course.id, lastPhase.id);
    navigate(`/projects/${projectId}/playbooks/${course.id}/active?phase=${lastPhase.id}`);
  };
  
  // Map the string array from course.targets to an array of objects for display
  const keyTargetsForDisplay = (course.keyTargets || []).map(target => 
    typeof target === 'string' 
      ? { title: target, description: '' } 
      : target
  );

  return (
    <div className="container py-4 sm:py-8 max-w-[1120px] mx-auto bg-white dark:bg-zinc-900 min-h-screen px-4 sm:px-6">
      {/* Mobile-optimized header */}
      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-[32px] leading-tight sm:leading-[38px] font-bold bg-gradient-to-r gradient-text flex-1">
          {course.title}
        </h1>
        
        {/* Mobile-optimized progress indicator */}
        <div className="bg-gradient-to-r from-white to-gray-50 dark:from-zinc-800 dark:to-zinc-700 border border-gray-200 dark:border-zinc-700 rounded-full h-[44px] sm:h-[32px] px-4 flex items-center gap-3 shadow-sm w-full sm:w-auto justify-center sm:justify-start">
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-gray-500 dark:text-zinc-400 px-1">
              Overall progress
            </div>
            <Badge className="bg-primary text-primary-foreground border-primary hover:bg-primary/90 px-2">
              {courseProgress.percentage}%
            </Badge>
          </div>
          <div className="h-4 w-px bg-gray-200 dark:bg-zinc-600"></div>
          <div className="text-xs font-medium text-gray-700 dark:text-zinc-300 px-1">
            {courseProgress.completed}/{courseProgress.total} tasks completed
          </div>
        </div>
      </div>
      
      {/* Mobile-optimized main content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 mb-6 sm:mb-8 overflow-hidden rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700">
        {/* Image section - mobile responsive */}
        <div className="lg:col-span-7 relative bg-white dark:bg-zinc-800">
          <div className="h-[240px] sm:h-[300px] lg:h-[336px]">
            <img 
              src={course.detail_image_url || `https://source.unsplash.com/random/1200x600/?${course.title.toLowerCase().replace(/\s+/g, '-')}`}
              alt={course.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = `https://source.unsplash.com/random/1200x600/?${course.title.toLowerCase().replace(/\s+/g, '-')}`;
              }}
            />
            
            {course.badge && (
              <Badge 
                className="absolute top-[8px] right-[8px] bg-primary hover:bg-primary/90 text-white font-medium px-3 h-6 leading-tight"
              >
                {course.badge}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Content section - mobile responsive */}
        <div className="lg:col-span-5 p-4 sm:p-6 bg-blue-50 dark:bg-blue-950/30">
          <h2 className="text-[16px] leading-[24px] font-bold mb-3 sm:mb-4 text-zinc-900 dark:text-zinc-100">Introduction</h2>
          <div className="text-zinc-700 dark:text-zinc-300 space-y-3 sm:space-y-4 max-w-prose">
            <p className="text-[15px] sm:text-[16px] leading-[22px] sm:leading-[24px]">{course.shortDescription || ''}</p>
          </div>
          
          {/* Mobile-optimized enrollment and action section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center my-4 sm:my-6 gap-4 sm:gap-3">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="flex -space-x-2">
                {demoEnrolledUsers.avatars.map((avatar, index) => (
                  avatar.type === 'image' ? (
                    <img 
                      key={index}
                      src={avatar.url}
                      alt={`User ${index + 1}`}
                      className="w-6 h-6 rounded-full border-2 border-white dark:border-zinc-800 object-cover shadow-sm"
                      onError={(e) => {
                        // Fallback for avatar images
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=User+${index + 1}&background=random`;
                      }}
                    />
                  ) : (
                    <div 
                      key={index}
                      className="w-6 h-6 rounded-full border-2 border-white dark:border-zinc-800 shadow-sm flex items-center justify-center text-[10px] font-medium text-white bg-blue-500 overflow-hidden"
                      style={{ backgroundImage: `url(${avatar.url})` }}
                    >
                      {avatar.initials}
                    </div>
                  )
                ))}
              </div>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                {demoEnrolledUsers.count} users enrolled
              </span>
            </div>

            <Button 
              onClick={courseProgress.percentage > 0 ? handleContinueCourse : handleStartCourse}
              size="lg"
              className="w-full sm:w-auto min-h-[44px] sm:min-h-auto"
            >
              {courseProgress.percentage > 0 ? 'Continue' : 'Start'}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Mobile-optimized key targets */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-[18px] sm:text-[20px] leading-[26px] sm:leading-[28px] font-semibold bg-gradient-to-r gradient-text mb-3 sm:mb-4">Key Targets</h3>
        <div className="grid grid-cols-1 gap-y-3 sm:gap-y-4 sm:grid-cols-2 sm:gap-x-6">
          {course?.keyTargets && course.keyTargets.length > 0 && (
            course.keyTargets.map((target, index) => (
              <div key={index} className="flex gap-3"> 
                <div className="flex-shrink-0 text-blue-600 dark:text-blue-400 mt-0.5">
                  <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 stroke-[2px]" />
                </div>
                <div>
                  {typeof target === 'string' ? (
                    <p className="text-zinc-900 dark:text-zinc-100 text-sm sm:text-base">{target}</p>
                  ) : (
                    <>
                      <h4 className="text-[15px] sm:text-[16px] leading-[22px] sm:leading-[24px] font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{target.title}</h4>
                      {target.description && (
                        <p className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px] text-zinc-600 dark:text-zinc-400">{target.description}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <div className="border-t border-gray-200 dark:border-zinc-700 my-6 sm:my-8"></div>
      
      {/* Mobile-optimized content phases */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-[18px] sm:text-[20px] leading-[26px] sm:leading-[28px] font-semibold bg-gradient-to-r gradient-text mb-4 sm:mb-5">Content</h2>
        <ul className="grid grid-cols-1 gap-y-3 sm:gap-y-4 sm:grid-cols-2 sm:gap-x-6">
          {(course.phases || []).map((phase, index) => (
            <li key={phase.id} className="text-[15px] sm:text-[16px] leading-[22px] sm:leading-[24px] font-semibold text-zinc-900 dark:text-zinc-100">
              <span className="font-bold">Phase {index + 1}</span> – {phase.title}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CourseDetailPage; 